import type { HeatmapCell, HeatmapData } from "./chartData";
import { heatmapCellKey } from "./chartData";
import type { DrillClick } from "./drilldown";
import { formatTagValue } from "../../tag-aliases/formatTagValue";

export type IssueSeverity = "critical" | "watch";

/** One attention-worthy result surfaced in the triage rail. */
export interface TriageIssue {
  /** Stable React key. */
  key: string;
  /** Display label - "Faithfulness" (ungrouped) or "Faithfulness / Billing" (grouped). */
  label: string;
  criteria: string;
  /** Present only when grouped. */
  group?: string;
  avg: number;
  count: number;
  /** Evaluations scoring below the threshold; null when the response carried no
   *  failure count and severity fell back to the mean. */
  failCount: number | null;
  /** failCount / count — what severity is judged on; null in the fallback. */
  failRate: number | null;
  severity: IssueSeverity;
  /** Drill into the offending traces (criteria drill ungrouped, group drill grouped). */
  drill: DrillClick;
}

export interface DeriveIssuesOptions {
  /** Below this score a single evaluation counts as a failure. Must be the
   *  threshold the cells' `belowCount` was counted against (the
   *  `below_threshold` sent to /insights/aggregate); it is only applied here in
   *  the mean fallback, for a response that carried no count. */
  threshold: number;
  /** Failing share at or above which a result group is "critical". */
  criticalRate: number;
  /** Failing share at or above which a result group is "watch". */
  watchRate: number;
  /** Suppress cells too small for a rate to mean anything. */
  minCount: number;
  /** criteria -> display label. */
  labels?: Record<string, string>;
  /** tag value -> display name, so a triage entry reads the same as the chart
   *  it came from. The issue's `group` stays the raw value it drills on. */
  tagAliases?: Record<string, string>;
  /** When set, cells are (criteria x group); issues drill by group. Absent -> per-criterion. */
  groupByKey?: string;
}

interface Verdict {
  severity: IssueSeverity | null;
  failCount: number | null;
  failRate: number | null;
}

/**
 * How badly a cell is doing, from the share of its evaluations below the
 * threshold — a count the server does (`below_count`), so it is exact.
 *
 * Without that count there is nothing to divide, so severity falls back to the
 * old mean comparison — which under-reports (a healthy-looking mean hides
 * failures) but is better than showing nothing at all. In the app both aggregate
 * fetches ask for the count, so the fallback only covers a response that
 * predates it.
 */
function judge(cell: HeatmapCell, opts: DeriveIssuesOptions): Verdict {
  const { threshold, criticalRate, watchRate } = opts;

  if (cell.belowCount == null) {
    const severity =
      cell.avg < threshold
        ? "critical"
        : cell.avg < threshold + 0.05
          ? "watch"
          : null;
    return { severity, failCount: null, failRate: null };
  }

  if (cell.count === 0) return { severity: null, failCount: 0, failRate: 0 };
  const failRate = cell.belowCount / cell.count;
  const severity =
    failRate >= criticalRate ? "critical" : failRate >= watchRate ? "watch" : null;
  return { severity, failCount: cell.belowCount, failRate };
}

/**
 * Turn a heatmap (grouped cells, or the ungrouped single-empty-group scorecard
 * data) into a ranked triage list: result groups failing often enough to be
 * worth someone's morning, worst-first. "Failing" is the share of a group's
 * evaluations scoring below `threshold` — not where its mean landed, which is a
 * different question than the rail and the KPI tile claim to answer, and one
 * that reads clean on exactly the unfiltered view users land on first (#1492).
 * A per-cell count floor suppresses samples too small to carry a rate.
 */
export function deriveIssues(
  data: HeatmapData,
  opts: DeriveIssuesOptions,
): TriageIssue[] {
  const { minCount, labels = {}, tagAliases = {}, groupByKey } = opts;
  const issues: TriageIssue[] = [];

  for (const cell of data.cells.values()) {
    if (cell.count < minCount) continue;
    const { severity, failCount, failRate } = judge(cell, opts);
    if (!severity) continue;

    const critLabel = labels[cell.criteria] ?? cell.criteria;
    const common = {
      criteria: cell.criteria,
      avg: cell.avg,
      count: cell.count,
      failCount,
      failRate,
      severity,
    };
    if (groupByKey) {
      issues.push({
        ...common,
        key: heatmapCellKey(cell.criteria, cell.group),
        label: `${critLabel} · ${formatTagValue(cell.group, tagAliases)}`,
        group: cell.group,
        drill: {
          type: "group",
          criteria: cell.criteria,
          groupKey: groupByKey,
          groupValue: cell.group,
        },
      });
    } else {
      issues.push({
        ...common,
        key: cell.criteria,
        label: critLabel,
        drill: { type: "criteria", criteria: cell.criteria },
      });
    }
  }

  // Worst-first: all criticals before watches, then the highest failure rate.
  // Mean-fallback issues (no rate) sort last within their severity, by score.
  return issues.sort((a, b) => {
    if (a.severity !== b.severity) return a.severity === "critical" ? -1 : 1;
    if (a.failRate != null && b.failRate != null) return b.failRate - a.failRate;
    if (a.failRate != null) return -1;
    if (b.failRate != null) return 1;
    return a.avg - b.avg;
  });
}
